/**
 * Per-speaker portrait framing overrides.
 *
 * Fields:
 *   fit      → CSS object-fit  ('cover' | 'contain')
 *   position → CSS object-position (e.g. 'center 22%', '20% 22%')
 *   scale    → OPTIONAL number (>1) — extra zoom via CSS transform on the img.
 *              1.4 crops out ~29% around the edges → the face fills the circle
 *              even when the source photo is a wider "chest-up" shot.
 *
 * DEFAULT for everyone unlisted: cover / center 22% (no extra zoom) — this
 * matches the framing the user liked on R' Yaacov Fried etc.
 */
export const DEFAULT_PORTRAIT_POSITION = 'center 22%';
export const DEFAULT_PORTRAIT_FIT = 'cover';
// Modest global bump — enough to trim shoulders/torso but leaves comfortable
// breathing room around the face + hat. Higher values (1.3+) crop the frame
// so tightly that portraits feel squished.
export const DEFAULT_PORTRAIT_SCALE = 1.12;

// Extra zoom for chest-up / wide portraits where the face is proportionally
// small in the source image. Pivot the scale from ~30% down (where faces
// typically sit in a standing portrait) so the face stays centered in the
// circle rather than getting scaled off the top.
const ZOOM_INTO_FACE = { fit: 'cover', position: 'center 30%', scale: 1.7 };

// For the very darkest / oddly-framed portraits where scale just enlarges an
// empty spot, fall back to `contain` so at least the whole image is visible.
const SHOW_WHOLE_IMAGE = { fit: 'contain', position: 'center' };

const OVERRIDES = {
  // Group photos — focus on the correct person
  "R' Singer":                 { fit: 'cover', position: '20% 22%' },   // person on left
  "R' IIlan Feder":            { fit: 'cover', position: '80% 22%' },   // person on right

  // Wider portraits — need extra zoom on the face
  "R' Moshe Kamin":            ZOOM_INTO_FACE,
  "R' Kaufman":                ZOOM_INTO_FACE,
  "R' Ahron Winter Zt'l":      ZOOM_INTO_FACE,
  "R' Moshe Chait":            SHOW_WHOLE_IMAGE,   // dark / oddly-framed portrait
  "R' Moshe Chait Zt'l":       SHOW_WHOLE_IMAGE,
  "R' Yehudah Jacobson":       ZOOM_INTO_FACE,
  "R' Herschel Welcher":       ZOOM_INTO_FACE,
  "R' Zvi Turk":               ZOOM_INTO_FACE,
  "R' Gedalya Glatt":          ZOOM_INTO_FACE,
  "R' Yisroel Niman":          ZOOM_INTO_FACE,
  "R' Aryeh Zev Ginzberg":     ZOOM_INTO_FACE,
  "R' Eliyahu Kaufman":        ZOOM_INTO_FACE,
  "R' Dovid Opoczynski":       ZOOM_INTO_FACE,
  "R' Avraham Baruch Rauch":   ZOOM_INTO_FACE,
  "R' Aryeh Rodin":            ZOOM_INTO_FACE,

  // Transparent-source portraits — force a solid white backdrop so the
  // Drive thumbnail's checkered "transparency" pattern doesn't show
  // through behind the person.
  "R' Chaim Tzvi Friedman":    { fit: 'cover', position: 'center 22%', scale: 1.12, bg: '#ffffff' },
};

const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Returns { fit, position, scale? } for a given speaker name.
 * Falls back to DEFAULT for anyone not explicitly overridden.
 */
export function getPortraitStyle(speakerName) {
  if (!speakerName) return { fit: DEFAULT_PORTRAIT_FIT, position: DEFAULT_PORTRAIT_POSITION, scale: DEFAULT_PORTRAIT_SCALE };
  const direct = OVERRIDES[speakerName];
  if (direct) return direct;
  const n = norm(speakerName);
  for (const [k, v] of Object.entries(OVERRIDES)) {
    if (norm(k) === n) return v;
  }
  return { fit: DEFAULT_PORTRAIT_FIT, position: DEFAULT_PORTRAIT_POSITION, scale: DEFAULT_PORTRAIT_SCALE };
}
